import React, { useState, useEffect } from "react";
import { useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { formatDate } from '../../utils/helper.js';
import { BOOKING_STATUSES } from "../../assets/utils/statusColors";
import PageHeader from "../../components/ui/PageHeader";
import StatusBadge from "../../components/ui/StatusBadge";
import Avatar from "../../components/ui/Avatar";
import StatCard from "../../components/ui/StatCard";
import { providerType } from "../comapnies/providerTypes";
import Swal from 'sweetalert2';

const currency = process.env.REACT_APP_CURRENCY || 'AED';
const money = (v) => (v != null ? `${currency} ${Number(v).toFixed(2)}` : '—');

const Row = ({ label, children, bold }) => (
  <div className="d-flex justify-content-between gap-4 py-2 border-bottom border-gray-100">
    <span className="text-muted text-nowrap">{label}</span>
    <span className={`text-dark text-end text-break ${bold ? 'fw-bolder' : 'fw-semibold'}`}>{children || '—'}</span>
  </div>
);

const Card = ({ title, sub, action, children, className = 'mb-4' }) => (
  <div className={`hp-card ${className}`}>
    <div className="hp-card-head">
      <div>
        <h3 className="hp-card-title">{title}</h3>
        {sub && <p className="hp-card-sub">{sub}</p>}
      </div>
      {action}
    </div>
    <div className="hp-card-body">{children}</div>
  </div>
);

const BookingDetails = () => {
  const { id } = useParams();
  const [isLoader, setIsLoader] = useState(true);
  const [request, setRequest] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (id) getBookingDetails();
  }, [id]);

  const getBookingDetails = async () => {
    try {
      setIsLoader(true);
      const { data } = await ApiService.request({ method: 'GET', url: `bookings/${id}` });
      if (data.status) {
        setRequest(data.data);
        setNewStatus(data.data?.status || '');
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to fetch booking details');
    } finally {
      setIsLoader(false);
    }
  };

  const updateStatus = async () => {
    if (!newStatus || newStatus === request.status) return;
    const result = await Swal.fire({
      title: `Mark booking as ${newStatus}?`,
      text: 'The customer and provider will be notified.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, update it',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    try {
      setIsUpdating(true);
      const { data } = await ApiService.request({
        method: 'POST',
        url: `bookings/${id}/status`,
        data: { status: newStatus },
      });
      if (data.status) {
        toast.success(data.message);
        setRequest(data.data || request);
        setNewStatus(data.data?.status || newStatus);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      // ApiService already shows the error toast
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoader || !request) {
    return (
      <div className="hp-page">
        {isLoader ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
            <Loader text="Loading booking..." size="40" />
          </div>
        ) : (
          <div className="hp-card hp-empty">
            <i className="bi bi-calendar-x"></i>
            Booking not found.
            <div className="mt-4"><Link to="/bookings" className="btn btn-sm btn-primary">Back to bookings</Link></div>
          </div>
        )}
      </div>
    );
  }

  const service = request.service_id || null;
  const company = request.company_id || null;
  const customer = request.user_id || null;
  const meta = request.meta_data || {};
  // Facility bookings happen at the customer's property (address holds a snapshot of it from booking time)
  const property = request.property_id || (request.location === 'Property' ? request.address : null);
  const address = !property && request.address && typeof request.address === 'object' ? request.address : null;
  const isFacility = request.job_type === 'facility' || company?.type === 'facility';
  const isCancelled = request.status === 'Cancelled';

  const hours = Number(meta.no_hours) || null;
  const professionals = Number(meta.no_professionals) || null;
  const duration = hours ? (service?.durations || []).find(d => Number(d.no_hours) === hours) : null;
  const rate = duration ? (duration.discount_price > 0 ? duration.discount_price : duration.regular_price) : null;

  const addressLine = address
    ? [address.apt_villa_no, address.building_name, address.street, address.area, address.city].filter(Boolean).join(', ')
    : (typeof request.address === 'string' ? request.address : null);
  const mapUrl = address?.lat && address?.lng ? `https://www.google.com/maps?q=${address.lat},${address.lng}` : null;

  // The booking exists, so every step up to its current status is reached even if no tracking row was saved
  const statusIndex = BOOKING_STATUSES.indexOf(request.status);
  const timeline = (request.timeline || []).map((step, i) => ({
    ...step,
    is_done: step.is_done || (!isCancelled && statusIndex >= i),
    created_at: step.created_at || (i === 0 ? request.createdAt : null),
  }));

  const statusOptions = [...BOOKING_STATUSES, ...(BOOKING_STATUSES.includes(request.status) ? [] : [request.status])];
  const provider = company ? providerType(company.type) : null;
  const media = Array.isArray(request.media_files) ? request.media_files : [];

  return (
    <div className="hp-page">
      <Link to="/bookings" className="text-muted fs-7 d-inline-flex align-items-center gap-1 mb-3">
        <i className="bi bi-arrow-left"></i> Back to bookings
      </Link>

      <PageHeader
        title={
          <span className="d-inline-flex align-items-center flex-wrap gap-3">
            Booking #{request.job_code}
            <StatusBadge status={request.status} />
            <span className="hp-chip fs-8 py-1">
              <i className={`bi ${isFacility ? 'bi-buildings' : 'bi-person-badge'}`}></i>
              {isFacility ? 'Facility' : 'Service Provider'}
            </span>
          </span>
        }
        subtitle={`Booked on ${formatDate(request.createdAt, 'DD MMM YYYY, hh:mm A')}`}
        actions={
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm w-auto"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={isUpdating}
              aria-label="Booking status"
            >
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              className="btn btn-sm btn-primary text-nowrap"
              onClick={updateStatus}
              disabled={isUpdating || !newStatus || newStatus === request.status}
            >
              {isUpdating ? <Loader size="16" color="white" text="Updating" /> : 'Update Status'}
            </button>
          </div>
        }
      />

      <div className="row g-4 mb-4">
        <div className="col-sm-6 col-xl-3">
          <StatCard label="Scheduled for" icon="bi-calendar-event" tone="info"
            value={<span className="fs-4">{request.date ? formatDate(request.date, 'DD MMM YYYY') : '—'}</span>}
            foot={request.time || 'Time not set'} />
        </div>
        <div className="col-sm-6 col-xl-3">
          <StatCard label="Duration" icon="bi-clock-history" tone="violet"
            value={<span className="fs-4">{hours ? `${hours} hour${hours > 1 ? 's' : ''}` : '—'}</span>}
            foot={professionals ? `${professionals} professional${professionals > 1 ? 's' : ''}` : null} />
        </div>
        <div className="col-sm-6 col-xl-3">
          <StatCard label="Total amount" icon="bi-wallet2" tone="brand"
            value={<span className="fs-4">{money(request.pay_amount)}</span>}
            foot={request.pay_method || (request.transaction_id ? 'Paid online' : 'Payment method not set')} />
        </div>
        <div className="col-sm-6 col-xl-3">
          <StatCard label={provider ? provider.singular : 'Provider'} icon="bi-building" tone="slate"
            value={<span className="fs-5 text-truncate d-block">{company?.name || 'Not assigned'}</span>}
            foot={company?.avg_rating ? <><i className="bi bi-star-fill text-warning me-1"></i>{Number(company.avg_rating).toFixed(1)}</> : null}
            to={company && provider ? `${provider.basePath}/${company._id}` : undefined} />
        </div>
      </div>

      <div className="row g-4">
        <div className="col-xl-8">
          <Card title="Service">
            {service ? (
              <>
                <div className="d-flex gap-4 flex-wrap">
                  {service.images?.[0] && (
                    <img src={service.images[0]} alt={service.name} className="rounded" style={{ width: 140, height: 105, objectFit: 'cover' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  )}
                  <div className="flex-grow-1" style={{ minWidth: 220 }}>
                    <div className="fs-4 fw-bold text-dark">{service.name}</div>
                    <div className="text-muted mb-3">
                      {service.category_id?.title || '—'}{service.sub_category_id ? ` › ${service.sub_category_id.title}` : ''}
                    </div>
                    {service.short_desc && <p className="text-gray-700 mb-0">{service.short_desc}</p>}
                  </div>
                </div>

                {service.durations?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-muted fs-7 fw-bold text-uppercase mb-2">Duration options</div>
                    <div className="d-flex flex-wrap gap-2">
                      {service.durations.map((d, i) => {
                        const selected = hours && Number(d.no_hours) === hours;
                        return (
                          <div key={i} className={`rounded px-4 py-2 border ${selected ? 'border-primary bg-light-primary' : 'border-gray-200'}`}>
                            <div className="fw-bold text-dark">
                              {d.no_hours}h {selected && <i className="bi bi-check-circle-fill text-primary ms-1"></i>}
                            </div>
                            <div className="fs-7">
                              {d.discount_price > 0 && d.discount_price < d.regular_price ? (
                                <>
                                  <span className="text-dark fw-semibold">{money(d.discount_price)}</span>
                                  <span className="text-muted text-decoration-line-through ms-2">{money(d.regular_price)}</span>
                                </>
                              ) : <span className="text-dark fw-semibold">{money(d.regular_price)}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {service.images?.length > 1 && (
                  <div className="d-flex flex-wrap gap-2 mt-5">
                    {service.images.slice(1).map((img, i) => (
                      <a key={i} href={img} target="_blank" rel="noreferrer">
                        <img src={img} alt="" className="rounded" style={{ width: 64, height: 64, objectFit: 'cover' }} />
                      </a>
                    ))}
                  </div>
                )}
              </>
            ) : <div className="text-muted">This service is no longer available.</div>}
          </Card>

          <Card title="Schedule & location"
            action={mapUrl && (
              <a href={mapUrl} target="_blank" rel="noreferrer" className="btn btn-sm hp-btn-outline">
                <i className="bi bi-geo-alt me-1"></i> Open in Maps
              </a>
            )}>
            <Row label="Date">{request.date ? formatDate(request.date, 'dddd, DD MMM YYYY') : null}</Row>
            <Row label="Time">{request.time}</Row>
            <Row label="Location">{request.location}</Row>
            {property && (
              <>
                <Row label="Property">{[property.building_name, property.unit_no && `Unit ${property.unit_no}`].filter(Boolean).join(', ')}</Row>
                <Row label="Area / Community">{property.area}</Row>
                <Row label="Unit type">{property.unit_type}</Row>
                {property.contact_number && <Row label="Property contact">{property.phone_code} {property.contact_number}</Row>}
              </>
            )}
            {address?.type && <Row label="Address type">{address.type}</Row>}
            {addressLine && <Row label="Address">{addressLine}</Row>}
            {address?.directions && <Row label="Directions">{address.directions}</Row>}
            {request.emirate_id?.name && <Row label="Emirate">{request.emirate_id.name}</Row>}
          </Card>

          <Card title="Customer notes">
            {request.notes
              ? <p className="text-gray-800 mb-0" style={{ whiteSpace: 'pre-line' }}>{request.notes}</p>
              : <div className="text-muted">No notes from the customer.</div>}
          </Card>

          <Card title="Photos & files" sub={media.length ? `${media.length} attached by the customer` : null} className="mb-4 mb-xl-0">
            {media.length > 0 ? (
              <div className="d-flex flex-wrap gap-3">
                {media.map((mf, idx) => (
                  mf.type === 'image' ? (
                    <a key={idx} href={mf.file} target="_blank" rel="noreferrer">
                      <img src={mf.file} alt={mf.name || 'media'} className="rounded border" style={{ width: 120, height: 120, objectFit: 'cover' }} />
                    </a>
                  ) : mf.type === 'video' ? (
                    <video key={idx} controls className="rounded border" style={{ width: 220, height: 120 }} src={mf.file}></video>
                  ) : (
                    <a key={idx} href={mf.file} target="_blank" rel="noreferrer" className="btn btn-sm hp-btn-outline align-self-start">
                      <i className="bi bi-paperclip me-1"></i>{mf.name || `Attachment ${idx + 1}`}
                    </a>
                  )
                ))}
              </div>
            ) : <div className="text-muted">No photos or files attached.</div>}
          </Card>
        </div>

        <div className="col-xl-4">
          <Card title="Progress">
            {isCancelled && (
              <div className="alert alert-danger py-2 px-3 fs-7 mb-4">
                <i className="bi bi-x-circle me-1"></i> This booking was cancelled.
              </div>
            )}
            <ul className="hp-timeline">
              {timeline.map(step => (
                <li key={step.status} className={step.is_done ? 'done' : ''}>
                  <span className="hp-tl-dot">{step.is_done && <i className="bi bi-check"></i>}</span>
                  <div className="hp-tl-title">{step.status}</div>
                  <div className="hp-tl-time">
                    {step.created_at ? formatDate(step.created_at, 'DD MMM YYYY, hh:mm A') : (step.is_done ? 'Done' : 'Pending')}
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Payment">
            {rate != null && professionals && (
              <div className="bg-light rounded px-4 py-3 mb-3 fs-7 text-gray-700">
                {money(rate)} × {professionals} professional{professionals > 1 ? 's' : ''} ({hours}h)
              </div>
            )}
            <Row label="Sub total">{money(request.sub_total)}</Row>
            <Row label="Service charges">{money(request.service_charges)}</Row>
            {request.cashback_used > 0 && <Row label="Cashback used">− {money(request.cashback_used)}</Row>}
            <div className="d-flex justify-content-between align-items-center pt-3 pb-2">
              <span className="fw-bold text-dark">Total</span>
              <span className="fs-3 fw-bolder text-dark">{money(request.pay_amount)}</span>
            </div>
            <Row label="Method">{request.pay_method}</Row>
            <Row label="Transaction ID">{request.transaction_id}</Row>
          </Card>

          <Card title="Customer">
            {customer ? (
              <>
                <Link to={`/users/${customer._id}`} className="d-flex align-items-center gap-3 mb-4">
                  <Avatar src={customer.picture} name={customer.name} size={44} />
                  <div className="min-w-0">
                    <div className="fw-bold text-dark text-hover-primary">{customer.name || '—'}</div>
                    <div className="text-muted fs-7 text-truncate">{customer.email}</div>
                  </div>
                </Link>
                <div className="d-flex gap-2">
                  {customer.phone && (
                    <a href={`tel:${customer.phone_code || ''}${customer.phone}`} className="btn btn-sm hp-btn-outline flex-grow-1">
                      <i className="bi bi-telephone me-1"></i> {customer.phone_code} {customer.phone}
                    </a>
                  )}
                  {customer.email && (
                    <a href={`mailto:${customer.email}`} className="btn btn-sm hp-btn-outline" title="Email">
                      <i className="bi bi-envelope"></i>
                    </a>
                  )}
                </div>
              </>
            ) : <div className="text-muted">Customer account not found.</div>}
          </Card>

          <Card title={provider ? provider.singular : 'Provider'} className="mb-0">
            {company ? (
              <>
                <Link to={`${provider.basePath}/${company._id}`} className="d-flex align-items-center gap-3 mb-4">
                  <Avatar src={company.picture} name={company.name} size={44} />
                  <div className="min-w-0">
                    <div className="fw-bold text-dark text-hover-primary">{company.name}</div>
                    <div className="text-muted fs-7 text-truncate">{company.email}</div>
                  </div>
                </Link>
                {company.service_type && <Row label="Service type">{company.service_type}</Row>}
                {company.avg_rating > 0 && (
                  <Row label="Rating"><i className="bi bi-star-fill text-warning me-1"></i>{Number(company.avg_rating).toFixed(1)}</Row>
                )}
                {company.phone && (
                  <a href={`tel:${company.phone_code || ''}${company.phone}`} className="btn btn-sm hp-btn-outline w-100 mt-4">
                    <i className="bi bi-telephone me-1"></i> {company.phone_code} {company.phone}
                  </a>
                )}
              </>
            ) : <div className="text-muted">No provider assigned to this booking yet.</div>}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
